import React from "react";
import FormInput from "@/components/FormInput";
import { INCENTIVE_STATUS } from "@/constants";
import { useFormContext } from "react-hook-form";
import { Box, Grid, Typography } from "@mui/material";
import FormCurrencyInput from "@/components/FormCurrencyInput";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { useAuthStore } from "@/store/authStore.ts";
import { ProjectRecord } from "@/types";
import { numberInputKeyDownValidation } from "@/utils/numberInputKeyDownValidation.ts";
import { EmailPatternValidation } from "@/validators/emailPatternValidation.ts";
import FormDatePicker from "@/components/FormDatePicker";

const FirstProjectForm = ({
  incentiveStatus,
  enrollmentStatus,
  isEdit
}: {
  incentiveStatus?: INCENTIVE_STATUS | undefined;
  enrollmentStatus?: ProjectRecord["enrolmentStatus"] | undefined;
  isEdit?: boolean;
}) => {
  const { watch, control } = useFormContext();
  const upfrontIncentivePayment = Number(watch("upfrontIncentivePayment"));
  const role = useAuthStore((state) => state.role);
  const isAdmin = role === "administrator";

  const isUpfrontIncentivePaymentDisabled =
    !isAdmin &&
    ((!!incentiveStatus && incentiveStatus !== INCENTIVE_STATUS.PENDING) ||
      (!!enrollmentStatus && enrollmentStatus !== "pending"));

  const isNameAndAddressDisabled =
    !isAdmin && incentiveStatus === INCENTIVE_STATUS.RESERVED;

  const isShowExtendProjectDueDate = isAdmin && incentiveStatus === INCENTIVE_STATUS.EXTENDED

  return (
    <>
      <FormInput
        name={"customerName"}
        control={control}
        label={"Customer name"}
        placeholder={"Add customer name"}
        disabled={isNameAndAddressDisabled}
        controlProps={{ rules: { required: "Required" } }}
      />
      <FormInput
        name={"address"}
        control={control}
        label={"Address"}
        placeholder={"Add address"}
        disabled={isNameAndAddressDisabled}
        controlProps={{ rules: { required: "Required" } }}
      />
      <Box sx={{ display: "flex", gap: "16px" }}>
        <FormInput
          name={"city"}
          control={control}
          label={"City"}
          placeholder={"Add city"}
          controlProps={{ rules: { required: "Required" } }}
        />
        <FormInput
          name={"zipCode"}
          control={control}
          type={"number"}
          label={"Zip code"}
          placeholder={"Add zip code"}
          onKeyDown={(e) =>
            numberInputKeyDownValidation(e, ["+", ".", ",", "'", '"'])
          }
          controlProps={{
            rules: {
              required: "Required",
              validate: (value) => {
                if (value?.length > 5) {
                  return "Zip code should be a max of 5 digits.";
                }
              },
            },
          }}
        />
      </Box>
      <FormInput
        name={"accountHash"}
        control={control}
        label={"Account #"}
        placeholder={"000000000000"}
        controlProps={{
          rules: {
            required: "Required",
            validate: (value) => {
              if (value?.length < 12) {
                return "Account # should be a minimum of 12 digits.";
              }
              if (value?.length > 13) {
                return "Invalid value";
              }
            },
          },
        }}
      />
      <FormInput
        name={"accountEmail"}
        control={control}
        label={"Customer email"}
        placeholder={"Add customer email"}
        controlProps={{
          rules: {
            required: "Required",
            ...EmailPatternValidation,
          },
        }}
      />
      <Typography
        sx={{ padding: "0 0 28px 0", display: "flex" }}
        variant={"bodyRegular"}
      >
        Existing Solar:
      </Typography>
      <FormInput
        name={"existingInverter"}
        control={control}
        label={"Existing Inverter"}
        placeholder={"Add existing inverter"}
        controlProps={{
          rules: {
            required: "Required",
          },
        }}
      />
      <Grid container columnSpacing={"8px"}>
        <Grid item xs={6} lg={4}>
          <FormInput
            type={"number"}
            name={"solarKwdc"}
            control={control}
            label={"kW-DC"}
            onKeyDown={numberInputKeyDownValidation}
            inputProps={{ min: 0 }}
            placeholder={"100"}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <FormInput
            type={"number"}
            name={"solarKwac"}
            control={control}
            label={"kW-AC"}
            onKeyDown={numberInputKeyDownValidation}
            placeholder={"100"}
            inputProps={{ min: 0 }}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <FormInput
            type={"number"}
            name={"solarAnnualProduction"}
            control={control}
            label={"Annual Production"}
            onKeyDown={numberInputKeyDownValidation}
            inputProps={{ min: 0 }}
            placeholder={"100"}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
      </Grid>
      <Typography
        sx={{ padding: "0 0 28px 0", display: "flex" }}
        variant={"bodyRegular"}
      >
        New Solar:
        <Typography sx={{ color: "red", paddingLeft: "4px" }}>*</Typography>
      </Typography>
      <Grid container columnSpacing={"8px"}>
        <Grid item xs={6} lg={4}>
          <FormInput
            type={"number"}
            name={"newSolarKwdc"}
            onKeyDown={numberInputKeyDownValidation}
            inputProps={{ min: 0 }}
            control={control}
            label={"kW-DC"}
            placeholder={"100"}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <FormInput
            type={"number"}
            name={"newSolarKwac"}
            onKeyDown={numberInputKeyDownValidation}
            inputProps={{ min: 0 }}
            control={control}
            label={"kW-AC"}
            placeholder={"100"}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <FormInput
            type={"number"}
            name={"newSolarAnnualProduction"}
            onKeyDown={numberInputKeyDownValidation}
            inputProps={{ min: 0 }}
            control={control}
            label={"Annual Production"}
            placeholder={"100"}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Grid>
      </Grid>
      <FormCurrencyInput
        name={"upfrontIncentivePayment"}
        control={control}
        label={"Upfront Incentive Payment"}
        placeholder={"$00.00"}
        maxValue={10000}
        defaultValue={
          upfrontIncentivePayment > 0
            ? formatCurrency(upfrontIncentivePayment)
            : ""
        }
        controlProps={{ rules: { required: "Required" } }}
        disabled={isUpfrontIncentivePaymentDisabled}
      />
      {isAdmin && isEdit && (
        <FormDatePicker
          control={control}
          name={"operationalizedDate"}
          label={"Operationalized Date"}
        />
      )}
        {isShowExtendProjectDueDate && (
            <>
                <Typography
                    sx={{ padding: "28px 0", display: "flex" }}
                    variant="bodyRegular"
                >
                    Extended due date:
                </Typography>
                <FormDatePicker
                    name="extendToDate"
                    control={control}
                    label="Extend To Date"
                    disablePast
                    controlProps={{
                      rules: { required: "Required" },
                    }}
                />
                <Box sx={{marginTop: "28px"}}>
                    <FormInput
                        name="extendToDateNote"
                        control={control}
                        label="Note for Contractor"
                        placeholder="Note"
                        multiline
                        rows={4}
                        controlProps={{
                          rules: { required: "Required" },
                        }}
                    />
                </Box>
            </>
        )}
    </>
  );
};

export default FirstProjectForm;
